import { useMemo, useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ClipboardList, Loader2, Mail, UserCheck } from 'lucide-react';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveApplicationNav } from '../../navigation/applicationNavigation';

const STATUS_LABEL = {
  SELECTING_ASSIGNEE: 'Selecting assignee',
  INVITE_PENDING: 'Waiting for email responses (first confirm wins)',
  SITE_VISIT_REQUESTED: 'Site visit requested ?? awaiting report',
};

function stepTitle(step) {
  const n = Number(step) || 0;
  if (n === 0) return 'Step 1 ?? Site visit: assign worker or expert (COV path uses experts)';
  if (n === 1) return 'Step 2 ?? After report: next compliance action';
  return `Step ${n + 1} — Further workflow`;
}

export default function AmendSsSiteVisitPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const applicationId = searchParams.get('applicationId') || '';
  const ssCode = (searchParams.get('ssCode') || '').trim();
  const situationKey = (searchParams.get('situationKey') || 'FIRST_VISIT').trim() || 'FIRST_VISIT';

  const [covRequired, setCovRequired] = useState(false);
  const [partyKind, setPartyKind] = useState('WORKER');
  const [selectedIds, setSelectedIds] = useState([]);
  const [manualId, setManualId] = useState('');

  const enabled = !!applicationId && !!ssCode;

  const { data: application } = useQuery({
    queryKey: ['compliance-amendment', applicationId],
    queryFn: async () => {
      const res = await api.get(`/compliance-amendments/${applicationId}`);
      return res.data;
    },
    enabled: !!applicationId,
    retry: false,
  });

  const { data: workflowPayload, isLoading: wfLoading } = useQuery({
    queryKey: ['amend-ss-visit', applicationId, ssCode, situationKey],
    queryFn: async () => {
      const res = await api.get(`/amend-ss-visit/application/${applicationId}/current`, {
        params: { ssCode, situationKey },
      });
      return res.data;
    },
    enabled,
    retry: false,
  });

  const row = workflowPayload?.data;
  const invites = workflowPayload?.invites || [];
  const assignee = workflowPayload?.assignee;

  useEffect(() => {
    if (row && typeof row.cov_required !== 'undefined') {
      setCovRequired(!!row.cov_required);
    }
  }, [row?.cov_required]);

  useEffect(() => {
    if (covRequired) setPartyKind('EXPERT');
  }, [covRequired]);

  const { data: workersData } = useQuery({
    queryKey: ['workers', 'amend-ss-visit'],
    queryFn: async () => {
      const res = await api.get('/workers', { params: { limit: 500, page: 1 } });
      return res.data?.data ?? [];
    },
    enabled,
    retry: false,
  });

  const { data: expertsData } = useQuery({
    queryKey: ['experts', 'amend-ss-visit', ssCode],
    queryFn: async () => {
      const res = await api.get('/experts', { params: { ssCode } });
      return res.data?.data ?? [];
    },
    enabled: enabled && partyKind === 'EXPERT',
    retry: false,
  });

  const workers = useMemo(() => {
    const list = Array.isArray(workersData) ? workersData : [];
    if (!ssCode) return list;
    return list.filter((w) => {
      const iqp = w.iqp || [];
      return iqp.some((x) => String(x.ss_code || '').replace(/\//g, '.') === ssCode.replace(/\//g, '.'));
    });
  }, [workersData, ssCode]);

  const experts = Array.isArray(expertsData) ? expertsData : [];

  const checklistUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (applicationId) p.set('applicationId', applicationId);
    p.set('ssCode', ssCode);
    p.set('situationKey', situationKey);
    return `/site-inspection-checklist?${p.toString()}`;
  }, [applicationId, ssCode, situationKey]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['amend-ss-visit', applicationId, ssCode, situationKey] });
  };

  const emailMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/amend-ss-visit/application/${applicationId}/email-invite`, {
        ssCode,
        situationKey,
        covRequired,
        partyType: partyKind,
        ids: selectedIds.map((id) => parseInt(id, 10)).filter(Boolean),
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Invites queued (check SMTP logs if email not configured)');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const manualMutation = useMutation({
    mutationFn: async () => {
      const id = parseInt(manualId, 10);
      if (!id) throw new Error('Select a person');
      const body = {
        ssCode,
        situationKey,
        covRequired,
        partyType: partyKind,
        workerId: partyKind === 'WORKER' ? id : undefined,
        expertId: partyKind === 'EXPERT' ? id : undefined,
      };
      const res = await api.post(`/amend-ss-visit/application/${applicationId}/manual-assign`, body);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Assignee set ?? site visit requested');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const reportMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/amend-ss-visit/application/${applicationId}/report-received`, {
        ssCode,
        situationKey,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Marked report received ?? advanced to next step');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message),
  });

  const toggleId = (id) => {
    const s = String(id);
    setSelectedIds((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const infoBody = (
    <>
      <p>
        This screen follows the amendment wireframe: per SS, assign someone for the site visit (worker or expert when
        COV / verification is needed), either by <strong>email race</strong> (first to confirm in the email wins; others
        get a “slot filled” message) or <strong>manual</strong> selection.
      </p>
      <p className="mt-2">
        When the assignee is set, status becomes <strong>Site visit requested</strong>. After their report (or the expert
        report) is received, use <strong>Report received</strong> to move to the next workflow step and assign again if
        needed.
      </p>
    </>
  );

  if (!applicationId || !ssCode) {
    return (
      <div>
        <ModulePageHeading
          resolveNav={resolveApplicationNav}
          title="SS workflow"
          infoTitle="SS workflow (amendment)"
          infoChildren={
            <>
              {infoBody}
              <p className="mt-3 text-gray-700">
                Open this page from <strong>Amend CS List</strong> by clicking a row in the selected SS list
                (requires a saved application ID and SS code).
              </p>
            </>
          }
        />
        <Link to="/application/amend-compliance-schedules" className="text-primary-700 underline mt-4 inline-block">
          Open Amend CS List
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ModulePageHeading
        resolveNav={resolveApplicationNav}
        title="SS workflow"
        infoTitle="SS workflow"
        infoChildren={
          <>
            <p className="text-sm">
              <span className="font-mono">{ssCode}</span>
              {situationKey ? ` · ${situationKey}` : ''}
            </p>
            {infoBody}
          </>
        }
      />

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 space-y-2">
        {application && (
          <p className="text-sm text-gray-600">
            {application.form11a_building_name || '—'} — {application.form11a_address || '—'}
          </p>
        )}
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to={checklistUrl} className="inline-flex items-center gap-1 text-primary-700 font-medium hover:underline">
            <ClipboardList className="w-4 h-4" />
            Open site inspection checklist (same SS / situation)
          </Link>
        </div>
      </div>

      {wfLoading && (
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading workflow…
        </div>
      )}

      {!wfLoading && row && (
        <>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 space-y-2">
            <p className="text-sm font-semibold text-indigo-950">{stepTitle(row.workflow_step)}</p>
            <p className="text-sm text-gray-800">
              Status: <strong>{STATUS_LABEL[row.status] || row.status}</strong>
              {assignee && (
                <>
                  {' '}
                  — Assignee: <strong>{assignee.name}</strong> ({assignee.type})
                </>
              )}
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="checkbox"
                checked={covRequired}
                onChange={(e) => setCovRequired(e.target.checked)}
                disabled={row.status === 'SITE_VISIT_REQUESTED'}
              />
              COV / verification path (use expert pool + email race or manual expert)
            </label>
          </div>

          {(row.status === 'SELECTING_ASSIGNEE' || row.status === 'INVITE_PENDING') && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email race
                </h2>
                <p className="text-xs text-gray-600">
                  Sends a link to each selected person; the first confirmation wins. Others receive a short “slot
                  filled” email when SMTP is configured.
                </p>
                <div className="flex gap-2 text-sm">
                  <button
                    type="button"
                    className={`px-3 py-1 rounded-lg border ${partyKind === 'WORKER' ? 'bg-primary-50 border-primary-300' : 'border-gray-200'}`}
                    onClick={() => {
                      setPartyKind('WORKER');
                      setSelectedIds([]);
                    }}
                  >
                    Workers (IQP)
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 rounded-lg border ${partyKind === 'EXPERT' ? 'bg-primary-50 border-primary-300' : 'border-gray-200'}`}
                    onClick={() => {
                      setPartyKind('EXPERT');
                      setSelectedIds([]);
                    }}
                  >
                    Experts
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-2 space-y-1 text-sm">
                  {(partyKind === 'WORKER' ? workers : experts).map((p) => (
                    <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(String(p.id))}
                        onChange={() => toggleId(p.id)}
                      />
                      <span>{p.name}</span>
                      <span className="text-gray-400 text-xs truncate">{p.email}</span>
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={emailMutation.isPending || selectedIds.length === 0}
                  onClick={() => emailMutation.mutate()}
                  className="w-full py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {emailMutation.isPending ? 'Sending…' : 'Send invite emails'}
                </button>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  Manual assign
                </h2>
                <p className="text-xs text-gray-600">Pick one worker or expert directly (no email race).</p>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                >
                  <option value="">— Select —</option>
                  {(partyKind === 'WORKER' ? workers : experts).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.email})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={manualMutation.isPending || !manualId}
                  onClick={() => manualMutation.mutate()}
                  className="w-full py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  {manualMutation.isPending ? 'Saving…' : 'Assign selected person'}
                </button>
              </div>
            </div>
          )}

          {invites.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-gray-800 mb-2">Invite log</p>
              <ul className="text-xs text-gray-700 space-y-1">
                {invites.map((i) => (
                  <li key={i.id}>
                    {i.party_name} — {i.status}
                    {i.email_sent_at ? ` (email sent)` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {row.status === 'SITE_VISIT_REQUESTED' && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-emerald-950">
                When the worker or expert report is on file, advance the workflow to the next step (new assignment if
                required).
              </p>
              <button
                type="button"
                disabled={reportMutation.isPending}
                onClick={() => reportMutation.mutate()}
                className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-50"
              >
                {reportMutation.isPending ? 'Saving…' : 'Report received ?? next step'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
